/**
 * uploadTelemetry – observability helpers for the media upload pipeline.
 *
 * Provides:
 * - request_id generation per upload
 * - Sentry breadcrumb tracking per stage
 * - Server fallback logging (best-effort, never blocks UI)
 * - PostHog enrichment helpers
 */

import * as Sentry from '@sentry/react';
import { logSystemEvent } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import type { UploadStage, UploadErrorKind } from './uploadErrors';

export interface UploadContext {
  request_id: string;
  owner_type: string;
  owner_id: string;
  draft_session_id: string | null;
  file_name: string;
  file_size: number;
  file_type: string;
  user_agent: string;
  platform: string;
  kind: string;
}

/**
 * Generate a unique request_id for correlating an upload across
 * PostHog, Sentry, and system_events.
 */
export function generateUploadRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Detect platform from user agent for analytics tagging.
 */
export function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/Samsung/i.test(ua)) return 'samsung';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Mac/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return 'windows';
  return 'other';
}

/**
 * Build an UploadContext for a file + upload options.
 */
export function buildUploadContext(
  requestId: string,
  file: File,
  opts: {
    ownerType: string;
    ownerId?: string | null;
    draftSessionId?: string | null;
    kind: string;
  },
): UploadContext {
  return {
    request_id: requestId,
    owner_type: opts.ownerType,
    owner_id: opts.ownerId || opts.draftSessionId || 'unknown',
    draft_session_id: opts.draftSessionId ?? null,
    file_name: file.name,
    file_size: file.size,
    file_type: file.type || 'unknown',
    user_agent: navigator.userAgent,
    platform: detectPlatform(),
    kind: opts.kind,
  };
}

/**
 * Add a Sentry breadcrumb for a stage transition.
 */
export function addStageBreadcrumb(
  stage: UploadStage,
  ctx: UploadContext,
  extra?: Record<string, unknown>,
): void {
  try {
    Sentry.addBreadcrumb({
      category: 'media_upload',
      message: `stage:${stage}`,
      level: stage === 'done' ? 'info' : 'debug',
      data: {
        request_id: ctx.request_id,
        owner_type: ctx.owner_type,
        file_name: ctx.file_name,
        file_type: ctx.file_type,
        file_size: ctx.file_size,
        platform: ctx.platform,
        ...extra,
      },
    });
  } catch {
    // best-effort
  }
}

/**
 * Report upload failure to Sentry with full context tags.
 */
export function reportUploadFailureToSentry(
  error: unknown,
  stage: UploadStage,
  errorKind: UploadErrorKind,
  ctx: UploadContext,
): void {
  try {
    Sentry.withScope((scope) => {
      scope.setTag('feature', 'media_upload');
      scope.setTag('action', 'upload_file');
      scope.setTag('upload_stage', stage);
      scope.setTag('error_kind', errorKind);
      scope.setTag('platform', ctx.platform);
      scope.setTag('request_id', ctx.request_id);
      scope.setContext('upload_context', {
        ...ctx,
        // Don't send full user_agent as tag, only in context
      });
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
  } catch {
    // best-effort
  }
}

/**
 * Track file picker opened (before file selection).
 */
export function trackPickerOpened(
  requestId: string,
  meta: {
    ownerType: string;
    ownerId?: string | null;
    draftSessionId?: string | null;
    surface: string;
  },
  trackFn: (event: string, props: Record<string, unknown>) => void,
): void {
  const platform = detectPlatform();
  trackFn('media_picker_opened', {
    request_id: requestId,
    owner_type: meta.ownerType,
    owner_id: meta.ownerId || meta.draftSessionId || 'unknown',
    draft_session_id: meta.draftSessionId ?? null,
    surface: meta.surface,
    userAgent: navigator.userAgent,
    platform,
  });
}

/**
 * Track file selection result (called on onChange, even if 0 files).
 */
export function trackFileSelected(
  requestId: string,
  files: File[],
  meta: { ownerType: string; ownerId?: string | null; draftSessionId?: string | null; surface: string },
  trackFn: (event: string, props: Record<string, unknown>) => void,
): void {
  const platform = detectPlatform();
  const base = {
    request_id: requestId,
    files_count: files.length,
    userAgent: navigator.userAgent,
    platform,
    owner_type: meta.ownerType,
    surface: meta.surface,
  };

  if (files.length > 0) {
    const f = files[0];
    trackFn('media_file_selected', {
      ...base,
      file_name: f.name,
      file_size_bytes: f.size,
      file_type: f.type || null,
    });
  } else {
    trackFn('media_file_selection_empty', {
      ...base,
      reason: 'empty_filelist',
    });
  }
}

export interface PickerTelemetryMeta {
  ownerType: string;
  ownerId?: string | null;
  draftSessionId?: string | null;
  surface: string;
}

/**
 * Track file dialog canceled (user closed picker without selecting).
 * Only fire if no onDrop resolved the session first.
 */
export function trackFileDialogCanceled(
  requestId: string,
  meta: PickerTelemetryMeta,
  trackFn: (event: string, props: Record<string, unknown>) => void,
): void {
  const platform = detectPlatform();
  trackFn('media_file_dialog_canceled', {
    request_id: requestId,
    owner_type: meta.ownerType,
    owner_id: meta.ownerId || meta.draftSessionId || 'unknown',
    draft_session_id: meta.draftSessionId ?? null,
    surface: meta.surface,
    userAgent: navigator.userAgent,
    platform,
  });
}

/**
 * Track CTA click that should open a file picker.
 * Fires even if the picker never opens (Samsung edge case).
 */
export function trackMediaUploadCtaClicked(
  meta: {
    surface: string;
    ownerType: string;
    ownerId?: string | null;
    draftSessionId?: string | null;
    producerId?: string;
    url?: string;
  },
  trackFn: (event: string, props: Record<string, unknown>) => void,
): void {
  const platform = detectPlatform();
  trackFn('media_upload_cta_clicked', {
    surface: meta.surface,
    owner_type: meta.ownerType,
    owner_id: meta.ownerId ?? meta.draftSessionId ?? 'unknown',
    draft_session_id: meta.draftSessionId ?? null,
    producer_id: meta.producerId ?? null,
    url: meta.url ?? (typeof window !== 'undefined' ? window.location.pathname : null),
    userAgent: navigator.userAgent,
    platform,
  });
}

/**
 * Server fallback: log upload failure to system_events.
 * Fire-and-forget. Never blocks UI.
 */
export function logUploadFailureToServer(
  stage: UploadStage,
  errorKind: UploadErrorKind,
  errorMessage: string,
  ctx: UploadContext,
  durationMs?: number,
): void {
  // Intentionally not awaited
  logSystemEvent({
    level: 'error',
    event_key: 'media_upload_failed',
    domain: 'media',
    entity_type: ctx.owner_type,
    entity_id: ctx.owner_id !== 'unknown' ? ctx.owner_id : undefined,
    message: `Upload failed at stage=${stage} kind=${errorKind}: ${errorMessage}`,
    request_id: ctx.request_id,
    context: buildSystemEventContext({
      feature: 'media_upload',
      action: 'upload',
      error_code: errorKind,
      surface: 'media_upload',
      stage,
      error_message: errorMessage,
      file_name: ctx.file_name,
      file_size: ctx.file_size,
      file_type: ctx.file_type,
      platform: ctx.platform,
      user_agent: ctx.user_agent,
      draft_session_id: ctx.draft_session_id,
      duration_ms: durationMs,
    }),
  }).catch(() => {
    // Swallowed – server logging must never break UX
  });
}
